<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    items?: string[]
    bottomOffset?: number
  }>(),
  {
    items: () => [],
    bottomOffset: 120,
  },
)

const activeIndex = ref(0)
const shownItemCount = ref(0)
const isVisible = ref(true)
const currentText = computed(() => props.items[activeIndex.value] || '')
const playbackItemLimit = computed(() => Math.min(props.items.length, 30))
const resolvedBottomOffset = computed(() => Math.max(0, Number(props.bottomOffset) || 0))
let playbackTimer: number | null = null

onMounted(() => {
  resetPlayback()
})

onBeforeUnmount(() => {
  clearPlaybackTimer()
})

watch(
  () => props.items,
  () => {
    resetPlayback()
  },
  { deep: true },
)

/** 清理当前轮播计时器，避免路由切换后继续更新已卸载组件。 */
function clearPlaybackTimer() {
  if (playbackTimer === null) {
    return
  }

  window.clearTimeout(playbackTimer)
  playbackTimer = null
}

/** 从第一条重新开始播放；单条展示 2 秒，多条每 1.8 秒切换。 */
function resetPlayback() {
  clearPlaybackTimer()
  activeIndex.value = 0
  shownItemCount.value = props.items.length > 0 ? 1 : 0
  isVisible.value = props.items.length > 0

  if (!isVisible.value) {
    return
  }

  scheduleNextItem(props.items.length === 1 ? 2000 : 1800)
}

/** 播放完当前数据或达到 30 条上限后隐藏飞屏。 */
function scheduleNextItem(delay: number) {
  playbackTimer = window.setTimeout(() => {
    if (shownItemCount.value >= playbackItemLimit.value) {
      isVisible.value = false
      playbackTimer = null
      return
    }

    activeIndex.value = (activeIndex.value + 1) % props.items.length
    shownItemCount.value += 1
    scheduleNextItem(1800)
  }, delay)
}
</script>

<template>
  <div
    v-show="isVisible && currentText"
    class="feedback-bullet-screen pointer-events-none fixed z-[23] flex h-[46px] w-[640px] items-center overflow-visible"
    :style="{ bottom: `${resolvedBottomOffset}px` }"
    aria-hidden="true"
  >
    <Transition name="feedback-bullet" mode="out-in">
      <p
        :key="activeIndex"
        class="flex h-[46px] max-w-full items-center overflow-hidden rounded-[100px] bg-[#112D73CC] px-[24px] py-[12px] text-center text-[14px] leading-[20px] text-ellipsis whitespace-nowrap text-white"
      >
        <span class="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
          {{ currentText }}
        </span>
      </p>
    </Transition>
  </div>
</template>

<style scoped lang="less">
.feedback-bullet-screen {
  inset-inline-start: max(24px, calc((100vw - 1200px) / 2));
}

.feedback-bullet-enter-active,
.feedback-bullet-leave-active {
  transition:
    opacity 0.45s ease,
    transform 0.45s ease;
}

.feedback-bullet-enter-from {
  opacity: 0.2;
  transform: translateX(48px);
}

.feedback-bullet-leave-to {
  opacity: 0.2;
  transform: translateX(-48px);
}

html[dir='rtl'] .feedback-bullet-enter-from {
  transform: translateX(-48px);
}

html[dir='rtl'] .feedback-bullet-leave-to {
  transform: translateX(48px);
}

@media (max-width: 799px) {
  .feedback-bullet-screen {
    inset-inline-start: 16px;
    width: 80%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .feedback-bullet-enter-active,
  .feedback-bullet-leave-active {
    transition: opacity 0.01ms linear;
  }
}
</style>
